/*
  GoRoam — entries in the cart (loaded by sections/gr-footer.liquid on every page).

  What it does, after every cart render:
    - Cart drawer: adds a tally panel under the drawer header ("15,250 entries · in this
      order · Giveaway #9 …" with the live multiplier chip) and a "+X entries" chip on each
      line. Free gifts get a "Free gift" chip instead.
    - Cart page: the same, under the page header and on each line.
    - Cross-sell cards in either place: adds "+X entries" next to the price. (FullStack already
      hides a cross-sell whose product is in the cart.)

  Where the numbers come from:
    - the cart itself: /cart.js (final_line_price is after discounts)
    - the multiplier: window.GoRoamEntries (Giveaway Gator), 1 if unknown
    - bonus entries per variant: the JSON map the footer writes ([data-gr-bonus-map])
  Formula per line: floor(line total in $) × multiplier + bonus(variant) × quantity.
  Gift lines and $0 lines earn nothing.

  Order doublers ("2x your entire order", Nov 3 2026): Giveaway Gator doubles the whole
  order's entries (quick-entry bonus entries included) and the doubler earns no entries of
  its own. Doublers are recognised by product handle (CFG.doublerHandles, default below;
  the footer config can override it). When one is in the cart: its line shows a
  "Doubles your order" chip, every other line and the tally are ×2 (limit one per order,
  so a second doubler does not double again), other cross-sell cards show doubled entries,
  and a doubler cross-sell card (matched by its variant select's data-handle) gets no chip.

  It is idempotent: panels/chips carry data-gr-* markers and are replaced, never duplicated.
*/
const fmt = (n) => Math.round(n).toLocaleString('en-US');

function readJSON(sel, fallback) {
  try { const el = document.querySelector(sel); return el ? JSON.parse(el.textContent) : fallback; } catch (e) { return fallback; }
}
const CFG = Object.assign({ prizeLine: '', tallyLabel: 'entries', bonusChip: 'bonus live', giftChip: 'Free gift', doublerChip: 'Doubles your order', doublerHandles: ['2x-your-entire-order'], legal: '', showPerLine: true }, readJSON('[data-gr-cart-config]', {}));
const BONUS = readJSON('[data-gr-bonus-map]', {});

let mult = 1;
function whenMult(cb) {
  if (window.GoRoamEntries) return window.GoRoamEntries.ready((m) => { if (m > 0) mult = m; cb(); });
  let tries = 0;
  const t = setInterval(() => {
    if (window.GoRoamEntries) { clearInterval(t); window.GoRoamEntries.ready((m) => { if (m > 0) mult = m; cb(); }); }
    else if (++tries > 50) { clearInterval(t); cb(); }
  }, 100);
}

const isGift = (item) => item && (item.properties?._is_gift === 'true' || item.properties?._is_gift === true);
const isDoubler = (item) => !!item && CFG.doublerHandles.includes(item.handle);
function lineEntries(item) {
  if (!item || isGift(item) || isDoubler(item) || !item.final_line_price) return 0;
  const dollars = Math.floor(item.final_line_price / 100);
  const bonus = parseInt(BONUS[String(item.variant_id)], 10) || 0;
  return dollars * mult + bonus * item.quantity;
}

let cartCache = null, fetching = null;
async function getCart() {
  if (fetching) return fetching;
  fetching = fetch('/cart.js', { headers: { Accept: 'application/json' } }).then((r) => r.json()).then((c) => { cartCache = c; return c; }).catch(() => cartCache).finally(() => { fetching = null; });
  return fetching;
}

/* ---------- DOM pieces ---------- */
function tallyHTML(total) {
  const bonus = mult > 1;
  return `<div class="grh-cart__tally">
    <div><b class="grh-cart__big">${fmt(total)} <small>${CFG.tallyLabel}</small></b>${CFG.prizeLine ? `<span>${CFG.prizeLine}</span>` : ''}</div>
    <div class="grh-cart__mult"${bonus ? '' : ' hidden'}>${fmt(mult)}X<em>${CFG.bonusChip}</em></div>
  </div>${CFG.legal ? `<p class="grh-cart__legal">${CFG.legal}</p>` : ''}`;
}
function upsertTally(anchor, where, total) {
  if (!anchor) return;
  let box = anchor.parentElement.querySelector(':scope > [data-gr-tally]');
  if (!box) { box = document.createElement('div'); box.className = 'grh-cart'; box.setAttribute('data-gr-tally', ''); box.setAttribute('aria-live', 'polite'); anchor.insertAdjacentElement(where, box); }
  box.innerHTML = tallyHTML(total);
}
function upsertChip(container, text, gift) {
  if (!container) return;
  let chip = container.querySelector(':scope > [data-gr-ent]');
  if (!text) { chip?.remove(); return; }
  if (!chip) { chip = document.createElement('span'); chip.setAttribute('data-gr-ent', ''); container.appendChild(chip); }
  chip.className = 'grh-cart grh-cart__ent' + (gift ? ' grh-cart__ent--gift' : '');
  chip.textContent = text;
}

function paintLines(root, items, lineSel, pricesSel, factor) {
  root.querySelectorAll(lineSel).forEach((el) => {
    const idx = parseInt(el.dataset.index, 10) - 1;
    const item = items[idx];
    const prices = el.querySelector(pricesSel);
    if (!prices) return;
    const gift = el.dataset.isGift === 'true' || isGift(item);
    if (gift) return upsertChip(prices, CFG.giftChip, true);
    if (isDoubler(item)) return upsertChip(prices, CFG.doublerChip, true);
    if (!CFG.showPerLine) return upsertChip(prices, '', false);
    const n = lineEntries(item) * factor;
    upsertChip(prices, n > 0 ? '+' + fmt(n) + ' ' + CFG.tallyLabel : '', false);
  });
}

function paintCrossSells(root, factor) {
  root.querySelectorAll('cross-sell-component').forEach((cs) => {
    const price = cs.querySelector('.price-container .price') || cs.querySelector('.price-container');
    if (!price) return;
    if ([...cs.querySelectorAll('[data-ref="variant-select"][data-handle]')].some((s) => CFG.doublerHandles.includes(s.dataset.handle))) { cs.querySelector('[data-gr-ent]')?.remove(); return; }
    let cents = 0, bonus = 0;
    cs.querySelectorAll('[data-ref="variant-select"]').forEach((s) => {
      const o = s.options && s.options[s.selectedIndex];
      cents += parseInt((o && o.dataset.price) || s.dataset.price, 10) || 0;
      bonus += parseInt(BONUS[String((o && o.value) || s.value)], 10) || 0;
    });
    if (!cents) { const m = (price.textContent || '').replace(/[^0-9.]/g, ''); cents = Math.round(parseFloat(m) * 100) || 0; }
    const n = (Math.floor(cents / 100) * mult + bonus) * factor;
    let chip = cs.querySelector('[data-gr-ent]');
    if (!n) { chip?.remove(); return; }
    if (!chip) { chip = document.createElement('span'); chip.setAttribute('data-gr-ent', ''); chip.className = 'grh-cart grh-cart__ent grh-cart__ent--xs'; price.parentElement.appendChild(chip); }
    chip.textContent = '+' + fmt(n) + ' ' + CFG.tallyLabel;
  });
}

/* ---------- paint ---------- */
let painting = false;
async function paint() {
  if (painting) return; painting = true;
  try {
    const cart = await getCart();
    if (!cart) return;
    const items = cart.items || [];
    const factor = items.some(isDoubler) ? 2 : 1;
    const total = items.reduce((s, it) => s + lineEntries(it), 0) * factor;

    const drawer = document.querySelector('cart-drawer');
    if (drawer) {
      const header = drawer.querySelector('.cart-drawer__header');
      if (header && items.length) upsertTally(header, 'afterend', total);
      else drawer.querySelector('[data-gr-tally]')?.remove();
      paintLines(drawer, items, '[data-ref="cart-item"]', '.cart-drawer__item-prices', factor);
      paintCrossSells(drawer, factor);
      // The drawer's upsell cards are one horizontal strip (gr-media.css); mark it as a scroller for the text-fit audit.
      drawer.querySelectorAll('.cross-sells__list').forEach((l) => l.setAttribute('data-scroll', ''));
    }
    const page = document.querySelector('cart-component');
    if (page) {
      const header = page.querySelector('.cart__header');
      if (header && items.length) upsertTally(header, 'afterend', total);
      else page.querySelector('[data-gr-tally]')?.remove();
      paintLines(page, items, '[data-ref="cart-item"]', '.cart__item-prices', factor);
      paintCrossSells(page, factor);
    }
  } finally { painting = false; }
}

let timer = null;
const schedule = () => { clearTimeout(timer); timer = setTimeout(paint, 80); };

whenMult(() => {
  paint();
  // FullStack fires this after every add / change; the drawer re-renders its inner HTML then.
  document.addEventListener('cart:updated', () => setTimeout(schedule, 50));
  document.addEventListener('cart:open', schedule);
  document.addEventListener('goroam:multiplier', (e) => { if (e.detail?.multiplier > 0) mult = e.detail.multiplier; schedule(); });
  // Safety net: anything that rewrites the drawer or cart (gift processing, section rendering).
  const watch = (el) => el && new MutationObserver((muts) => {
    if (muts.some((m) => [...m.addedNodes].some((n) => n.nodeType === 1 && !n.hasAttribute?.('data-gr-tally') && !n.hasAttribute?.('data-gr-ent')))) schedule();
  }).observe(el, { childList: true, subtree: true });
  watch(document.querySelector('cart-drawer'));
  watch(document.querySelector('cart-component'));
});
